import { ROLES } from "./roles";
import { DESTINATIONS } from "./destinations";

export const JOLLY_MULTIPLIER = 0.95;
// Final crew score = individual quality + role fit + balance between the five areas.
export const WEIGHTS = { quality: 0.25, fit: 0.5, balance: 0.25 };
// Weight of the slot holder in an area; the rest comes from the other members' skill.
const PRIMARY_WEIGHT = 0.75;
// "Ottimo" area threshold: also the minimum for every area to reach Laugh Tale.
export const AREA_OTTIMO = 81;

const SKILLS = ["captain", "rightHand", "combat", "navigation", "support", "specialist"];

export const AREAS = [
  { key: "command", roles: ["captain", "rightHand"], skill: "captain" },
  { key: "combat", roles: ["combat"], skill: "combat" },
  { key: "navigation", roles: ["navigation"], skill: "navigation" },
  { key: "support", roles: ["support"], skill: "support" },
  { key: "specialist", roles: ["specialist"], skill: "specialist" },
];

const mean = (xs) => xs.reduce((s, v) => s + v, 0) / xs.length;
const topMean = (xs, n) => mean([...xs].sort((a, b) => b - a).slice(0, n));

const jollyBase = (stats) => stats.jolly ?? topMean(SKILLS.map((k) => stats[k]), 3) * JOLLY_MULTIPLIER;

// Off-role: the character's real competence in that role, reduced (secondary skills still count).
const offRole = (raw) => Math.max(5, raw * 0.75 - 5);

export const isNaturalRole = (character, roleKey) => character.natural.includes(roleKey);

export const roleValue = (character, roleKey) => {
  const raw = roleKey === "jolly" ? jollyBase(character.stats) : character.stats[roleKey];
  return isNaturalRole(character, roleKey) ? raw : offRole(raw);
};

const power = (character) => topMean(SKILLS.map((k) => character.stats[k]), 2);

const secondaryHelp = (assignments, area) =>
  topMean(
    ROLES.filter((r) => !area.roles.includes(r.key)).map((r) => assignments[r.key].stats[area.skill]),
    2,
  );

export const areaScores = (assignments) =>
  AREAS.map((a) => {
    const primary = mean(a.roles.map((r) => roleValue(assignments[r], r)));
    return { key: a.key, value: PRIMARY_WEIGHT * primary + (1 - PRIMARY_WEIGHT) * secondaryHelp(assignments, a) };
  });

// assignments: { [roleKey]: character } with all 7 roles filled
export const crewBreakdown = (assignments) => {
  const areas = areaScores(assignments);
  const [low1, low2] = areas.map((a) => a.value).sort((a, b) => a - b);
  const quality = mean(ROLES.map((r) => power(assignments[r.key])));
  const fit = mean(ROLES.map((r) => roleValue(assignments[r.key], r.key)));
  const balance = (low1 + low2) / 2;
  const score = WEIGHTS.quality * quality + WEIGHTS.fit * fit + WEIGHTS.balance * balance;
  const allNatural = ROLES.every((r) => isNaturalRole(assignments[r.key], r.key));
  return { score, quality, fit, balance, areas, perfect: allNatural && low1 >= AREA_OTTIMO };
};

// Laugh Tale needs the top score AND a perfect crew (all natural roles, no area below Ottimo).
export const getCrewDestination = (assignments) => {
  const { score, perfect } = crewBreakdown(assignments);
  let index = 0;
  DESTINATIONS.forEach((d, i) => {
    if (score >= d.min) index = i;
  });
  const last = DESTINATIONS.length - 1;
  return index === last && !perfect ? last - 1 : index;
};
